/*
  Spray field: a fine grid of droplets with the A Fan symbol sitting a little brighter than the mist,
  and a live fan spraying across it from the tip. Ported from brand/round-3 (r3.template.html).

  Markup: <canvas data-spray-field data-bg="#0D0D0D" data-fg="243,241,236" data-cx=".74" data-top=".05"
                  data-size=".6" data-wmax=".6" data-rate="6" data-alpha=".05" data-cell="6" data-input="#selector"></canvas>
  All data attributes are optional. Defaults are deliberately quiet (Jacob, 17 Oct 2026: "very, very subtly",
  about 5% behind the image): alpha .05, a slow trickle from the tip, no shimmer, a gentle pointer trail.
  Turn things up per use with data-alpha, data-rate and data-pointer (droplets per frame under the pointer).
  data-still draws one frame and never animates. data-fixed skips the fade in and the small-screen layout.
  Pauses off screen. Draws one still frame under prefers-reduced-motion or Save-Data.
*/
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches || !!(navigator.connection && navigator.connection.saveData);
  const mobile = matchMedia('(max-width: 749px)').matches;
  // Symbol A in a 100 x 100 box: solid fan wedge, small counter, wet-edge gap.
  const K = 47 / 92;
  function inFan(x, y) {
    if (y < 4 || y > 96) return 0;
    if (Math.abs(x - 50) > K * (y - 4)) return 0;
    if (y >= 66 && y <= 73) return 0;
    if (y > 36 && y < 58 && Math.abs(x - 50) < K * 0.9 * (y - 36)) return 0;
    return 1;
  }

  function Field(cv, o) {
    const ctx = cv.getContext('2d');
    const alpha = o.alpha ?? 0.05, rate = o.rate ?? 6, pen = o.pointer ?? 3, still = reduce || mobile || o.still;
    let W, H, cell, cols, rows, b, base, mask, ox, oy, sc, parts = [], visible = true, ptr = null, ramp = still || o.fixed ? 1 : 0, last = 0;
    function layout() {
      const dpr = Math.min(2, devicePixelRatio || 1), r = cv.getBoundingClientRect();
      W = Math.max(1, r.width); H = Math.max(1, r.height);
      cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cell = o.cell || (W < 600 ? 5 : 6);
      cols = Math.ceil(W / cell); rows = Math.ceil(H / cell);
      const n = cols * rows; b = new Float32Array(n); base = new Float32Array(n); mask = new Uint8Array(n);
      const small = W < 700 && !o.fixed;
      const ah = small ? Math.min(H * (o.sizeSm || 0.46), W * 0.86) : Math.min(H * (o.size || 0.84), W * (o.wmax || 0.62));
      sc = ah / 100; ox = W * (small ? 0.5 : (o.cx ?? 0.5)) - 50 * sc; oy = H * (small ? (o.topSm ?? 0.12) : (o.top ?? 0.06));
      for (let r2 = 0; r2 < rows; r2++) for (let c = 0; c < cols; c++) {
        const k = r2 * cols + c, mx = (c * cell + cell / 2 - ox) / sc, my = (r2 * cell + cell / 2 - oy) / sc;
        mask[k] = inFan(mx, my);
        let v = 0;
        if (mask[k]) v = 0.3 + 0.12 * Math.min(1, Math.abs(mx - 50) / (K * Math.max(my - 4, 1)));
        else { const s = Math.sin(c * 0.07 + r2 * 0.04) * Math.cos(r2 * 0.11 - c * 0.025) + 0.6 * Math.sin(c * 0.019 + r2 * 0.13); v = s > 1.0 ? 0.05 : 0.012; }
        base[k] = v; b[k] = still || o.fixed ? v : 0;
      }
    }
    function emit(n, ex, ey, spread) {
      for (let i = 0; i < n; i++) {
        const u = Math.random() * 2 - 1, a = Math.PI / 2 + Math.sign(u) * Math.pow(Math.abs(u), 0.6) * spread;
        const v = (5 + Math.random() * 5) * Math.max(0.7, sc / 6);
        parts.push({ x: ex, y: ey, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0, br: 6 + Math.random() * 10 });
      }
    }
    function step() {
      if (ramp < 1) ramp = Math.min(1, ramp + 0.014);
      emit(rate, ox + 50 * sc, oy + 4 * sc, Math.atan(K));
      if (ptr && pen) emit(pen, ptr.x, ptr.y, 0.45);
      for (let i = parts.length - 1; i >= 0; i--) {
        const q = parts[i]; q.life++;
        if (q.life > q.br) { q.vx = q.vx * 0.975 + (Math.random() - 0.5) * 0.45; q.vy = q.vy * 0.975 + 0.04; }
        q.x += q.vx; q.y += q.vy;
        if (q.x < 0 || q.y < 0 || q.x >= W || q.y >= H || q.life > 120) { parts.splice(i, 1); continue; }
        const k = ((q.y / cell) | 0) * cols + ((q.x / cell) | 0), hit = mask[k] ? 0.95 : 0.4;
        if (b[k] < hit) b[k] = hit;
      }
      for (let k = 0; k < b.length; k++) b[k] += (base[k] * ramp - b[k]) * 0.06;
      for (let i = 0, n = Math.round(rate * 1.5); i < n; i++) { const k = (Math.random() * b.length) | 0; if (mask[k]) b[k] = Math.min(0.8, b[k] + 0.25); }
    }
    function draw() {
      if (o.bg) { ctx.fillStyle = o.bg; ctx.fillRect(0, 0, W, H); } else ctx.clearRect(0, 0, W, H);
      const rgb = o.fg || '243,241,236';
      const lv = 10, buckets = Array.from({ length: lv }, () => []);
      for (let k = 0; k < b.length; k++) { const v = b[k]; if (v < 0.02) continue; buckets[Math.min(lv - 1, (v * lv) | 0)].push(k); }
      for (let i = 0; i < lv; i++) {
        const L = buckets[i]; if (!L.length) continue;
        const v = (i + 0.5) / lv, s = Math.max(0.8, cell * (0.18 + 0.5 * v));
        ctx.fillStyle = `rgba(${rgb},${Math.min(1, (0.25 + v * 0.9) * alpha).toFixed(3)})`;
        ctx.beginPath();
        for (const k of L) { const c = k % cols, r2 = (k / cols) | 0; const cx = c * cell + cell / 2, cy = r2 * cell + cell / 2; ctx.moveTo(cx + s / 2, cy); ctx.arc(cx, cy, s / 2, 0, 6.2832); }
        ctx.fill();
      }
    }
    function loop(now) { requestAnimationFrame(loop); if (!visible || document.hidden || now - last < 30) return; last = now; step(); draw(); }
    layout();
    new ResizeObserver(() => { layout(); draw(); }).observe(cv);
    new IntersectionObserver((es) => { visible = es[0].isIntersecting; }).observe(cv);
    if (o.input && !still) {
      o.input.addEventListener('pointermove', (e) => { const r = cv.getBoundingClientRect(); ptr = { x: e.clientX - r.left, y: e.clientY - r.top }; }, { passive: true });
      o.input.addEventListener('pointerleave', () => { ptr = null; });
    }
    if (still) draw(); else { for (let i = 0; i < 30; i++) step(); draw(); requestAnimationFrame(loop); }
    return { burst() { for (let i = 0; i < 14; i++) emit(40, ox + 50 * sc, oy + 4 * sc, Math.atan(K)); } };
  }

  const num = (v) => (v === undefined || v === '' ? undefined : Number(v));
  document.querySelectorAll('[data-spray-field]').forEach((cv) => {
    const d = cv.dataset;
    if (d.sprayOn) return; // the script can be included by more than one section
    d.sprayOn = '1';
    const f = Field(cv, {
      bg: d.bg, fg: d.fg, cell: num(d.cell), cx: num(d.cx), top: num(d.top), size: num(d.size), wmax: num(d.wmax),
      rate: num(d.rate), alpha: num(d.alpha), pointer: num(d.pointer), still: d.still !== undefined, sizeSm: num(d.sizeSm), topSm: num(d.topSm), fixed: d.fixed !== undefined,
      input: d.input ? cv.closest(d.input) : null,
    });
    const btn = d.burst && document.querySelector(d.burst);
    if (btn) btn.addEventListener('click', () => f.burst());
  });
})();
